import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MdWork, MdList } from 'react-icons/md';
import { FiAlertCircle, FiHelpCircle } from 'react-icons/fi';
import { SidebarItem, MenuAccordionItem } from '../SidebarComponents';

// API URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const WorkersMenu = () => {
  const location = useLocation();
  const [pendingHelpRequests, setPendingHelpRequests] = useState(0);
  const [pendingCancellations, setPendingCancellations] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState({
    helpResponse: null,
    cancellationResponse: null,
    error: null
  });

  // Fetch worker help requests count
  const fetchHelpRequests = async () => {
    const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');
    if (!userToken_TOVIAPPS) return { count: 0 };
    
    const url = `${API_BASE_URL}api/menu/requests-status/worker-help`;    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${userToken_TOVIAPPS}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch help requests: ${response.status}`);
    const data = await response.json();
    return data;
  };

  // Fetch worker cancellation requests count
  const fetchCancellations = async () => {
    const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');
    if (!userToken_TOVIAPPS) return { count: 0 };
    
    const url = `${API_BASE_URL}api/menu/requests-status/worker-cancellation`;    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${userToken_TOVIAPPS}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch cancellations: ${response.status}`);
    const data = await response.json();
    return data;
  };

  const hasFetched = useRef(false);

  // Fetch request counts from API
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchCounts = async () => {
      try {
        setIsLoading(true);
        
        // Fetch both counts
        const helpResult = await fetchHelpRequests();
        const cancellationResult = await fetchCancellations();
        
        // Save response for debugging
        setDebugInfo({
          helpResponse: helpResult,
          cancellationResponse: cancellationResult,
          error: null
        });
       
        // Backend returns count field directly in the response
        setPendingHelpRequests(helpResult.count ?? 0);
        setPendingCancellations(cancellationResult.count ?? 0);
        
      } catch (error) {
        console.error('Error fetching request counts:', error);
        // Set defaults in case of error
        setPendingHelpRequests(0);
        setPendingCancellations(0);
        setDebugInfo(prev => ({
          ...prev,
          error: error.message
        }));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCounts();
    
    // Set up an interval to refresh the counts every 5 minutes
    const intervalId = setInterval(fetchCounts, 5 * 60 * 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Calculate total pending count
  const pendings = pendingHelpRequests + pendingCancellations;
  
  // Check if current route is active
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Check if an accordion is active based on subroutes
  const isAccordionActive = (parentPath) => {
    return location.pathname.startsWith(parentPath);
  };

  // Only show debug in development
  const showDebug = process.env.NODE_ENV === 'development';

  return (
    <>
      <MenuAccordionItem
        icon={MdWork}
        label="Contratados"
        isActive={isAccordionActive('/contratados')}
        hasBadge={true}
        badgeCount={isLoading ? '...' : pendings}
        defaultIsOpen={isAccordionActive('/contratados')}
      >
        <SidebarItem 
          icon={MdList} 
          label="Lista de Contratados" 
          to="/contratados/list" 
          active={isActive('/contratados/list')} 
          isSubmenuItem
        />
        <SidebarItem 
          icon={FiAlertCircle} 
          label="Cancelamentos" 
          to="/contratados/cancelamento" 
          active={isActive('/contratados/cancelamento')} 
          isSubmenuItem
          hasBadge={true}
          badgeCount={isLoading ? '...' : pendingCancellations}
        />
        <SidebarItem
          icon={FiHelpCircle} 
          label="Preciso de Ajuda" 
          to="/contratados/preciso-ajuda" 
          active={isActive('/contratados/preciso-ajuda')} 
          isSubmenuItem
          hasBadge={true}
          badgeCount={isLoading ? '...' : pendingHelpRequests}
        />
      </MenuAccordionItem>
    </>
  );
};

export default WorkersMenu;